import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { createSealNumberApi } from "../../services/_sealNumber";
import { successToast, errorToast } from "../Toast";

const initialValues = {
  prefix: "",
  suffix: "",
  seal_no: "",
};

interface AddSealNumberModalProps {
  addSealNumberModalStatus: boolean;
  closeSealNumberModal: () => void;
  onSealNumberCreated: any;
}
const validationSchema = Yup.object().shape({
  prefix: Yup.string().required("Prefix is required"),
  suffix: Yup.string()
    .matches(/^[0-9]+$/, "Enter a valid number")
    .required("Suffix is required"),
});

const AddSealNumberModal: React.FC<AddSealNumberModalProps> = ({
  addSealNumberModalStatus,
  closeSealNumberModal,
  onSealNumberCreated,
}) => {
  const [submitLoader, setSubmitLoader] = useState(false);

  const handleSubmit = async (values: any, { resetForm }: any) => {
    setSubmitLoader(true);
    const formattedPrefix = values.prefix.toUpperCase();
    const formattedSuffix = values.suffix.padStart(2, "0");
    const newSealData = {
      prefix: formattedPrefix,
      suffix: formattedSuffix,
      seal_no: `${formattedPrefix}${formattedSuffix}`,
    };

    try {
      const result: any = await createSealNumberApi([newSealData]);
      if (result.isSuccess) {
        successToast(result.message);
        resetForm({values: initialValues});
        closeSealNumberModal();
        onSealNumberCreated(result.data);
      } else {
        errorToast(result.message);
      }
    } catch (err) {
      errorToast("Something went wrong while creating the seal number");
    } finally {
      setSubmitLoader(false);
    }
  };

  if (!addSealNumberModalStatus) return null;

  return (
    <>
      <div className="modal-backdrop fade show"></div>
      <div
        className="modal fade show"
        tabIndex={-1}
        style={{ display: addSealNumberModalStatus ? "block" : "none" }}
        aria-hidden={!addSealNumberModalStatus}
        role="dialog"
      >
        <div className="modal-dialog modal-lg modal-simple modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-body p-4">
              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={closeSealNumberModal}
              ></button>
              <div className="text-center mb-2">
                <h5 className="mb-2">Add Seal Number</h5>
              </div>

              <div className="modal-body p-0">
                <Formik
                  enableReinitialize
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue, handleChange }) => (
                    <Form className="row g-3">
                      {/* Prefix */}
                      <div className="col-12 col-md-6">
                        <label
                          htmlFor="prefix"
                          className="form-label"
                          style={{ fontSize: "0.9375rem" }}
                        >
                          Prefix <span className="text-danger">*</span>
                        </label>
                        <Field
                          id="prefix"
                          name="prefix"
                          type="text"
                          className="form-control"
                          placeholder="Enter prefix"
                          onChange={(e: any) => {
                            const upper = e.target.value.toUpperCase();
                            handleChange(e);
                            setFieldValue("prefix", upper);
                            setFieldValue(
                              "seal_no",
                              `${upper}${values.suffix || ""}`
                            );
                          }}
                        />
                        <ErrorMessage
                          name="prefix"
                          component="div"
                          className="text-danger"
                        />
                      </div>

                      {/* Suffix */}
                      <div className="col-12 col-md-6">
                        <label
                          htmlFor="suffix"
                          className="form-label"
                          style={{ fontSize: "0.9375rem" }}
                        >
                          Suffix <span className="text-danger">*</span>
                        </label>
                        <Field
                          id="suffix"
                          name="suffix"
                          type="text"
                          className="form-control"
                          placeholder="Enter numeric part"
                          onChange={(e: any) => {
                            handleChange(e);
                            setFieldValue(
                              "seal_no",
                              `${values.prefix.toUpperCase() || ""}${
                                e.target.value
                              }`
                            );
                          }}
                        />
                        <ErrorMessage
                          name="suffix"
                          component="div"
                          className="text-danger"
                        />
                      </div>

                      {/* Preview */}
                      <div className="col-12">
                        <label
                          className="form-label"
                          style={{ fontSize: "0.9375rem" }}
                        >
                          Full Seal Number : 
                        </label>
                        <Field
                          id="seal_no"
                          name="seal_no"
                          type="text"
                          className="form-control"
                          disabled
                        />
                      </div>

                      {/* Buttons */}
                      <div className="col-12 text-center mt-3">
                        <button
                          type="submit"
                          className="btn btn-primary me-2"
                          disabled={submitLoader}
                        >
                          {submitLoader && (
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          )}
                          Add Seal Number
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={closeSealNumberModal}
                        >
                          Cancel
                        </button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddSealNumberModal;
